import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useAccessToken, useQueryParam, useViewport } from '@chilecompra/react-kit'
import { Card, Breadcrumb, Breadcrumbs, Button, Toast, Typography } from '@chilecompra/react-kit/components'

import routes from '../../config/settings/routes'
import { SURVEY_ORIGIN_DICTIONARY } from '../../config/settings/constants'
import CustomIframe from '../../components/Custom/Iframe'
import DownloadComprobanteLink from '../../components/Custom/DownloadComprobanteLink'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'
import { formatTypeCurrency } from '../../modules/utils/formatTypeCurrency'
import {
  ButtonsRow,
  DetailCaption,
  DetailLabel,
  DetailTable,
  DetailValue,
  DetailWrapper,
  Grid,
  ImgOnboarding01,
  Root,
  Row
} from './SellerQuotePublishedSuccessfully.styles'

const FALLBACK = 'Sin información'

const formatMontoOfertado = (montoTotal, moneda) => {
  if (montoTotal == null || !moneda) return FALLBACK

  return formatTypeCurrency(moneda, montoTotal, true) || FALLBACK
}

/**
 * The SellerQuotePublishedSuccessfully's container.
 */
const SellerQuotePublishedSuccessfully = () => {
  const accessToken = useAccessToken()
  const quoteCode = useQueryParam('quote')
  const navigate = useNavigate()

  const {
    size: { isDownToMedium, isDownToTiny }
  } = useViewport()

  const { comprobanteEnvio } = useSelector(state => state.sendSellerQuoteDialog)

  const handleGoBackSearchQuote = () => navigate(routes.search)
  const handleGoSummary = () => navigate(routes.quoteSummary.replace(/:quoteId/, quoteCode))

  const detailRows = [
    { label: 'ID cotización', value: comprobanteEnvio?.codigoSolicitud || quoteCode || FALLBACK },
    { label: 'Responsable de la cotización', value: comprobanteEnvio?.responsable || FALLBACK },
    { label: 'Convocatoria', value: comprobanteEnvio?.convocatoria || FALLBACK },
    {
      label: 'Monto total ofertado',
      value: formatMontoOfertado(comprobanteEnvio?.montoTotal, comprobanteEnvio?.moneda)
    },
    {
      label: 'Cantidad de productos ofertados',
      value:
        comprobanteEnvio?.cantidadProductosOfertados != null
          ? String(comprobanteEnvio.cantidadProductosOfertados)
          : FALLBACK
    },
    { label: 'Fecha y hora de envío', value: comprobanteEnvio?.fechaHoraEnvio || FALLBACK },
    { label: 'Fecha y hora de cierre', value: comprobanteEnvio?.fechaHoraCierre || FALLBACK }
  ]

  return (
    <Root>
      <Breadcrumbs onClick={handleGoBackSearchQuote}>
        <Breadcrumb>Escritorio</Breadcrumb>
        <Breadcrumb>Compra ágil</Breadcrumb>
      </Breadcrumbs>

      <Grid container justifyContent="center">
        <Card color="blank" enableBorder margin="24px 0 0" padding="32px" width={isDownToMedium ? '100%' : '900px'}>
          <Row>
            <ImgOnboarding01 src="/assets/images/onboarding-01.svg" />

            <Typography fontWeight="bold" margin="0 0 8px" variant="h4">
              Cotización enviada correctamente
            </Typography>
          </Row>

          <DetailWrapper>
            <DetailTable>
              <thead>
                <tr>
                  <DetailCaption colSpan={2}>Detalle de la cotización</DetailCaption>
                </tr>
              </thead>
              <tbody>
                {detailRows.map(row => (
                  <tr key={row.label}>
                    <DetailLabel>{row.label}</DetailLabel>
                    <DetailValue>{row.value}</DetailValue>
                  </tr>
                ))}
              </tbody>
            </DetailTable>

            {comprobanteEnvio?.correoNotificacion ? (
              <Toast margin="0 0 16px" severity="info">
                Enviamos el comprobante de tu cotización a tu correo electrónico. Recuerda mantenerlo actualizado.
              </Toast>
            ) : null}

            <Typography color="black2" margin="0 0 16px" variant="body2">
              Recuerda que los criterios de selección en una Compra Ágil no se basan exclusivamente en el precio más
              bajo; también pueden considerar aspectos como requisitos técnicos, plazos de entrega, entre otros, y son
              responsabilidad de cada organismo comprador.
            </Typography>

            <Typography color="black2" variant="body2">
              Puedes revisar el estado de tu participación en cualquier momento desde Mercado Público, en el módulo de
              Compra Ágil o en tu escritorio proveedor.
            </Typography>

            <ButtonsRow>
              <Button
                color="primary"
                margin={isDownToTiny ? '0 0 16px' : '0 16px 0 0'}
                onClick={handleGoSummary}
                width={isDownToTiny ? '100%' : undefined}
              >
                Ver cotización enviada
              </Button>
              <Button
                color="primary"
                onClick={handleGoBackSearchQuote}
                variant="outlined"
                width={isDownToTiny ? '100%' : undefined}
              >
                Volver a Compra Ágil
              </Button>
            </ButtonsRow>

            <DownloadComprobanteLink
              align="center"
              codigoSolicitud={quoteCode}
              cotizacionId={comprobanteEnvio?.idCotizacion}
              label="Descargar comprobante de cotización"
            />
          </DetailWrapper>
        </Card>

        <ProtectByFeatures rule={features => features['enable.survey'] && !!quoteCode}>
          <CustomIframe accessToken={accessToken} origin={SURVEY_ORIGIN_DICTIONARY.SELLER} quoteCode={quoteCode} />
        </ProtectByFeatures>
      </Grid>
    </Root>
  )
}

export default SellerQuotePublishedSuccessfully
